import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../ui/accordion";

export function PageHero({ kicker, title, subtitle, image, logo, testid = "page-hero" }) {
  return (
    <section data-testid={testid} className="relative pt-[72px]">
      <div className="relative min-h-[52vh] w-full overflow-hidden">
        {logo ? (
          <div className="absolute inset-0 bg-ink">
            <div
              className="absolute inset-0 opacity-[0.14]"
              style={{
                backgroundImage: `url(${logo})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundSize: "min(90%, 900px)",
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
          </div>
        ) : (
          <>
            <img src={image} alt={title} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 hero-overlay" />
          </>
        )}
        <div className="absolute inset-0 grain opacity-[0.15]" />
        <div className="relative container-x h-full flex flex-col justify-end pt-24 pb-14">
          <Reveal>
            {kicker && <div className="kicker text-flame mb-4">{kicker}</div>}
            <h1 className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl text-balance">
              {title}
            </h1>
            {subtitle && <p className="mt-5 text-white/75 text-base md:text-lg max-w-2xl leading-relaxed">{subtitle}</p>}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function SectionHead({ kicker, title, desc, center = false, dark = false }) {
  return (
    <div className={`${center ? "text-center mx-auto" : ""} max-w-3xl`}>
      {kicker && <div className="kicker text-flame mb-4">{kicker}</div>}
      <h2 className={`font-display font-bold text-3xl md:text-4xl tracking-tight ${dark ? "text-white" : "text-ink"}`}>{title}</h2>
      {desc && <p className={`mt-4 text-base md:text-lg leading-relaxed ${dark ? "text-white/70" : "text-muted-foreground"}`}>{desc}</p>}
    </div>
  );
}

export function FaqSection({ faqs, kicker = "FAQ", title = "Frequently asked questions", dark = false }) {
  if (!faqs?.length) return null;
  return (
    <section className={`${dark ? "bg-charcoal" : "bg-background"} border-t border-border`} data-testid="faq-section">
      <div className="container-x py-20">
        <div className="kicker text-flame">{kicker}</div>
        <h2 className={`mt-2 font-display font-bold text-3xl tracking-tight ${dark ? "text-white" : "text-ink"}`}>{title}</h2>
        <Accordion type="single" collapsible className="mt-8 max-w-3xl">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className={`text-left font-semibold hover:text-flame ${dark ? "text-white" : "text-ink"}`}>{f.q}</AccordionTrigger>
              <AccordionContent className={`text-base leading-relaxed ${dark ? "text-white/70" : "text-muted-foreground"}`}>{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function CTABand() {
  return (
    <section data-testid="cta-band" className="bg-ink relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-10" />
      <div className="container-x py-20 relative grid gap-8 lg:grid-cols-12 items-center">
        <div className="lg:col-span-8">
          <div className="kicker text-flame mb-4">Your Dedicated Solutions Manager</div>
          <h2 className="font-display font-extrabold text-white text-3xl md:text-5xl tracking-tight text-balance">
            Get value, not a catalog.
          </h2>
          <p className="mt-4 text-white/70 max-w-xl">
            Tell us about your operation and we'll build a customized readiness plan around your fleet, jobsites, and environmental risks.
          </p>
        </div>
        <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
          <Link to="/request-quote" data-testid="cta-quote" className="inline-flex items-center justify-center gap-2 bg-flame text-white px-6 py-4 font-semibold hover:bg-flame/90 transition-colors">
            Request a Quote <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link to="/contact" data-testid="cta-contact" className="inline-flex items-center justify-center gap-2 border border-white/25 text-white px-6 py-4 font-semibold hover:border-flame hover:text-flame transition-colors">
            Talk with Our Team
          </Link>
        </div>
      </div>
    </section>
  );
}
